import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from 'react-router';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { mdiCheckboxBlank, mdiCheckboxMarked, mdiDelete } from '@mdi/js';
import { motion } from 'framer-motion';

import { ENV_FRONTEND_URL } from 'env';
import {
    INotificationListItemFragment,
    INotificationsNotificationTypeChoices,
    DeleteNotificationsDocument,
    SetNotificationReadStatusDocument,
} from 'graphql/types';
import { ListItemButton, ListItemText } from 'common/components/ListItem';
import { getNotificationTypeTranslationKey } from 'common/utils/notification';
import { IconButton } from 'common/components/IconButton';
import { useUnreadNotificationCount } from 'common/hooks/useUnreadNotificationCount';
import { useApolloError } from 'common/hooks/useApolloError';
import { Tooltip } from 'common/components/Tooltip';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { EGroupFormTab } from 'user/types';

interface IProps {
    disableActions?: boolean;
    notification: INotificationListItemFragment;
    onClick?(): void;
    onLoading?(isLoading: boolean): void;
}

export const NotificationListItem = ({
    disableActions,
    notification,
    onClick,
    onLoading,
}: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const client = useApolloClient();
    const { showApolloError } = useApolloError();
    const [setNotificationReadStatus] = useMutation(
        SetNotificationReadStatusDocument
    );
    const [deleteNotifications, { loading: deleteNotificationsLoading }] =
        useMutation(DeleteNotificationsDocument);
    const { updateUnreadNotificationCount } = useUnreadNotificationCount();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();
    const [actionIsFocused, setActionIsFocused] = useState(false);

    const {
        title,
        content,
        readOn,
        type,
        extraData: extraDataJson,
        creationDate,
    } = notification;
    const notificationTypeTranslationKey =
        getNotificationTypeTranslationKey(type);

    const extraData = JSON.parse(extraDataJson || '{}');
    const { url, group_id: groupId } = extraData || {};

    const isSkillGroupBelowMinimum =
        type === INotificationsNotificationTypeChoices.SkillGroupBelowMinimum &&
        !!groupId;

    const isClickable = !!url || isSkillGroupBelowMinimum;

    const isExternalUrl = useMemo(
        () =>
            [
                INotificationsNotificationTypeChoices.ReportExportCompleted,
                INotificationsNotificationTypeChoices.UserExportCompleted,
                INotificationsNotificationTypeChoices.CourseExportCompleted,
                INotificationsNotificationTypeChoices.CourseCompleted,
                INotificationsNotificationTypeChoices.EnrollmentExportCompleted,
                INotificationsNotificationTypeChoices.PrivateChatUnreadMessage,
                INotificationsNotificationTypeChoices.TrainingChatUnreadMessage,
                INotificationsNotificationTypeChoices.GroupChatUnreadMessage,
            ].includes(type),
        [type]
    );

    /**
     * Update the readOn field in the cache and the unread notification count
     * @param isRead - Whether the notification is read
     * @param isError - Whether the mutation failed
     */
    const updateNotificationReadOn = (isRead: boolean, isError?: boolean) => {
        let readOn = isRead ? new Date().toISOString() : null;
        let count = isRead ? -1 : 1;

        // Use the opposite values for count and readOn to reset if the mutation fails
        if (isError) {
            readOn = isRead ? null : new Date().toISOString();
            count = count === 1 ? -1 : 1;
        }

        client.cache.modify({
            id: client.cache.identify(notification),
            fields: {
                readOn: () => readOn,
            },
        });

        updateUnreadNotificationCount(count);
    };

    const handleSetNotificationReadStatus = async (
        e: React.MouseEvent,
        isRead: boolean
    ) => {
        e.stopPropagation();

        try {
            await setNotificationReadStatus({
                variables: { ids: [notification.id], isRead },
                optimisticResponse: () => {
                    updateNotificationReadOn(isRead);

                    return {
                        setNotificationReadStatus: {
                            __typename: 'SetNotificationReadStatus',
                            ok: true,
                        },
                    };
                },
            });
        } catch (error) {
            showApolloError(error);

            updateNotificationReadOn(isRead, true);
        }

        return;
    };

    const handleDeleteNotifications = async (e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            await deleteNotifications({
                variables: { ids: [notification.id] },
                update: (cache) => {
                    cache.evict({ fieldName: 'notifications' });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        // Count is not impacted if already read
        if (readOn) return;

        updateUnreadNotificationCount(-1);

        return;
    };

    const handleClick = async (e: React.MouseEvent) => {
        try {
            await handleSetNotificationReadStatus(e, true);
        } catch (error) {
            showApolloError(error);
        }

        if (isSkillGroupBelowMinimum) {
            openGlobalDrawer({
                type: IGlobalDrawerType.Group,
                itemId: groupId,
                location: { ...location, hash: '' },
                props: {
                    initialTab: EGroupFormTab.Skills,
                    onClose: () =>
                        closeGlobalDrawer({ type: IGlobalDrawerType.Group }),
                },
            });

            navigate(
                { pathname: location.pathname, search: location.search },
                { replace: true }
            );

            return;
        }

        if (isExternalUrl) {
            if (
                type === INotificationsNotificationTypeChoices.CourseCompleted
            ) {
                const newUrl = `/${url}?next=${encodeURIComponent(
                    window.location.href
                )}`;

                window.location.href = newUrl;

                return;
            }

            window.open(url, '_blank');

            return;
        }

        const newUrl = url.replace(ENV_FRONTEND_URL, '');

        navigate(newUrl);

        onClick?.();
    };

    const actionsDisabled = disableActions || deleteNotificationsLoading;

    useEffect(() => {
        // Notify the parent component that the actions are loading
        // to disable the bulk actions
        onLoading?.(deleteNotificationsLoading);
    }, [deleteNotificationsLoading]);

    return (
        <motion.div
            initial="initListItemButton"
            tabIndex={0}
            whileFocus="showActions"
            whileHover="showActions"
        >
            <ListItemButton
                sx={{
                    px: 2,
                    py: 1,
                    cursor: isClickable ? 'pointer' : 'default',

                    '&:hover': {
                        backgroundColor: !isClickable
                            ? 'transparent'
                            : 'action.hover',
                    },
                }}
                tabIndex={-1}
                onClick={isClickable ? handleClick : undefined}
            >
                <Box sx={{ width: 10, mr: 2 }}>
                    {!readOn && (
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: 'error.main',
                            }}
                        />
                    )}
                </Box>

                <ListItemText showFullSecondary>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                        {notificationTypeTranslationKey && (
                            <Typography
                                color="text.secondary"
                                sx={{ lineHeight: 1.4 }}
                                variant="caption"
                            >
                                {translate(notificationTypeTranslationKey)}
                            </Typography>
                        )}

                        <Typography
                            color="text.secondary"
                            sx={{ ml: 'auto' }}
                            variant="caption"
                        >
                            {DateTime.fromISO(creationDate).toRelative()}
                        </Typography>
                    </Box>

                    <Stack direction="column" sx={{ pr: 2, spacing: 0.5 }}>
                        <Typography
                            color={readOn ? 'text.secondary' : 'text.primary'}
                            dangerouslySetInnerHTML={{ __html: title }}
                            variant="body2"
                        />

                        {!!content && (
                            <Typography
                                color={
                                    readOn ? 'text.secondary' : 'text.primary'
                                }
                                dangerouslySetInnerHTML={{ __html: content }}
                                sx={{ fontSize: '1.4rem' }}
                                variant="body2"
                            />
                        )}
                    </Stack>

                    <motion.div
                        animate={
                            actionIsFocused
                                ? 'showActions'
                                : 'initListItemButton'
                        }
                        style={{
                            position: 'absolute',
                            right: '5px',
                            top: '5px',
                        }}
                        transition={{ duration: 0.2 }}
                        variants={{
                            initListItemButton: {
                                opacity: 0,
                            },
                            showActions: {
                                opacity: 1,
                            },
                        }}
                    >
                        <Stack
                            direction="row"
                            divider={
                                <Divider flexItem orientation="vertical" />
                            }
                            sx={{
                                backgroundColor: 'common.white',
                                borderColor: 'grey.300',
                                borderRadius: 1,
                                borderWidth: 1,
                                borderStyle: 'solid',
                            }}
                        >
                            <Tooltip
                                title={
                                    !!readOn
                                        ? translate(
                                              'notificationsDrawer.markAsUnread'
                                          )
                                        : translate(
                                              'notificationsDrawer.markAsRead'
                                          )
                                }
                            >
                                <Box component="span">
                                    <IconButton
                                        disabled={actionsDisabled}
                                        iconPath={
                                            !!readOn
                                                ? mdiCheckboxBlank
                                                : mdiCheckboxMarked
                                        }
                                        iconSize="1.8rem"
                                        onBlur={() => setActionIsFocused(false)}
                                        onClick={(e: React.MouseEvent) =>
                                            handleSetNotificationReadStatus(
                                                e,
                                                !!readOn ? false : true
                                            )
                                        }
                                        onFocus={() => setActionIsFocused(true)}
                                    />
                                </Box>
                            </Tooltip>

                            <Tooltip
                                title={translate('notificationsDrawer.delete')}
                            >
                                <Box component="span">
                                    <IconButton
                                        disabled={actionsDisabled}
                                        iconPath={mdiDelete}
                                        iconSize="2rem"
                                        onBlur={() => setActionIsFocused(false)}
                                        onClick={handleDeleteNotifications}
                                        onFocus={() => setActionIsFocused(true)}
                                    />
                                </Box>
                            </Tooltip>
                        </Stack>
                    </motion.div>
                </ListItemText>
            </ListItemButton>
        </motion.div>
    );
};
